<script setup lang="ts">
import useCpBem from '~/composables/useCpBem';
import CpSectionHeader from '../CpSectionHeader/CpSectionHeader.vue';
import CpTextParser from '~/components/cp/CpTextParser/CpTextParser.vue';
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';

const useSyndicatedContentFrame = (url?: string) => {
    if (!url) {
        return null;
    }
    const {status} = useFetch<unknown>(url);
    return computed(()=> status.value === 'success' ? url: null);
};

const props = defineProps<{
  kind: 'SyndicatedContent'|'Text'
  url?: string
  text?: string
}>();

const {b, e, em} = useCpBem('cpd-section-pdp-long-description');
const iframeSrc = useSyndicatedContentFrame(props.url);
const iframeRef = ref<HTMLIFrameElement | null>(null);

const conversionMap = {
  '&aacute;': 'á',
  '&Aacute;': 'Á',
  '&eacute;': 'é',
  '&Eacute;': 'É',
  '&iacute;': 'í',
  '&Iacute;': 'Í',
  '&oacute;': 'ó',
  '&Oacute;': 'Ó',
  '&uacute;': 'ú',
  '&Uacute;': 'Ú',
  '&ntilde;': 'ñ',
  '&Ntilde;': 'Ñ',
  '&quot;': '"',
  '&uuml;': 'ü',
  '&amp;': '&',
  '&deg;': '°',
  '&iquest;': '¿',
  '&nbsp;': ' ',
  '<p>': '',
  '</p>': '',
  '<!DOCTYPE html>': '',
  '<html>': '',
  '</html>': '',
  '<body>': '',
  '</body>': '',
  '<strong>': '*',
  '</strong>': '*',
  '<b>': '*',
  '</b>': '*',
  '<br />\n<br />': '\n',
  '<br />\n': '\n',
  '<br />\n <br />\n': '\n\n',
  '<br><br>': '\n\n',
  '<br>': '\n',
  '<ul>': '',
  '</ul>': '',
  '<li>': '• ',
  '</li>': '',
};

const cleanDescription = (text: string) => {
  return Object.entries(conversionMap).reduce(
    (result, [encoded, decoded]) => result.replaceAll(encoded, decoded),
    text);
};

const cleanHtml = (htmlString: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  // remove html from text
  let text = cleanDescription(tempDiv.textContent || tempDiv.innerText || '').trim();
  // Clean up temp element to prevent memory leaks
  tempDiv.innerHTML = '';
  // replace multiple white spaces for one
  text = text.replace(/[ \t]+/g, ' ');
  // replace more that 2 line breaks for 2
  text = text.replace(/\n{3,}/g, '\n\n');
  // remove white space if have 2 line breaks before
  return text.replace(/\n{2}\s+/g, '\n\n');
};
const hasChildrenRef = ref(false);
const hasChildren = computed(() => hasChildrenRef.value);

/* Containers for external providers */
const containers = ref<HTMLElement[]>([]);
const observer = ref<MutationObserver | null>(null);

function updateHasChildren() {
    hasChildrenRef.value = containers.value.some((container) => container?.children.length > 0) || false;
    containers.value.forEach((container) => {
        if (container?.children.length <= 0) {
            container.style.display = 'none';
        } else {
            container.style.padding = '16px';
        }
    });
}

function cleanupContainerStyles() {
    containers.value.forEach((container) => {
        if (container) {
            container.style.display = '';
            container.style.padding = '';
        }
    });
    // Clean up scripts from GTM
    document.querySelectorAll('script[data-syndicated-id]').forEach((el) => el.remove());
}

const adjustIframeHeight = () => {
    const iframe = iframeRef.value;
    if (iframe && iframe.contentWindow) {
        const contentHeight = iframe.contentWindow.document.body.scrollHeight;
        iframe.style.height = `${contentHeight + 20}px`;
    }
};

onMounted(() => {
    updateHasChildren();
    containers.value = [
        document.getElementById('ccs-inline-content'),
        document.getElementById('flix-inpage'),
        document.getElementById('spexWidget'),
        document.getElementById('cpIcecatLive'),
    ].filter(Boolean) as HTMLElement[];

    // Create observer only when mounted
    observer.value = new MutationObserver(updateHasChildren);

    containers.value.forEach((container) => {
        if (container && observer.value) {
            observer.value.observe(container, {childList: true});
        }
    });
});

onBeforeUnmount(() => {
    // Clean up observer
    if (observer.value) {
        observer.value.disconnect();
        observer.value = null;
    }

    // Clean up container styles
    cleanupContainerStyles();

    // Clear container references
    containers.value = [];
});
</script>

<template>
    <div :class="b">
        <CpSectionHeader :title="'Detalles del producto'" type="h2" />
        <div id="ccs-inline-content" :class="e`provider-container`" />
        <div id="flix-inpage" :class="e`provider-container`" />
        <div id="spexWidget" :class="e`provider-container`" />
        <section id="cpIcecatLive" data-cp-dynamic-script :class="e`provider-container`" />
        <div v-if="!hasChildren" :class="e`container`">
            <div :class="em(`body-container`, { hideBorder: iframeSrc })">
                <iframe v-if="kind === 'SyndicatedContent' && iframeSrc"
                    id="iframeLongDesc"
                    ref="iframeRef"
                    frameborder="0"
                    scrolling="no"
                    :class="e`iframe`"
                    data-testid="iframeLongDesc"
                    :src="iframeSrc"
                    @load="adjustIframeHeight"
                />
                <div v-else
                    style="white-space: pre-wrap; line-height: 0;"
                >
                    <CpTextParser :text="cleanHtml(`${text}`)" variant="body" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.cpd-section-pdp-long-description {
  display: flex;
  flex-direction: column;
  &__container {
    width: 100%;
    padding: 16px;
  }
  &__body-container {
    width: 100%;
    border: 1px solid var(--color-neutral-extra-light);
    border-radius: $cp-gutter-small;
    padding: $cp-gutter-small;
    &--hide-border {
      border: none;
    }
  }
  &__provider-container {
    display: block;
  }
  &__iframe {
    display: inline;
    width: 100%;
    min-height: 600px;
    max-height: fit-content;
    border: none;
  }
}
</style>
